import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { GroupForm } from '.';
import * as api from '../../api/app/groupApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useTranslation } from '../../contexts/translation';
import { Group, GroupInput } from '../../types/app/group.types';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/group.functions';
import { paths } from '../../utils/core/routes';
import { useStyles } from './GroupForm.styles';

export function GroupDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<GroupInput | null>(null);
  const { mutate: mutateUpdateGroup } = useMutation(api.updateGroup);
  const { data, isLoading, isError } = useQuery<Group>(['groups', id], () =>
    api.getGroup(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<GroupInput> = (data: GroupInput) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
    });

    mutateUpdateGroup(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.group.item-edited'), { variant: 'success' });
          navigate(paths.GROUP_LIST);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <PageHeader title={t('page.group.headline-edit')} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <GroupForm
                onSubmit={handleOnSubmit}
                onBack={() => navigate(paths.GROUP_LIST)}
                defaultValues={defaultValues}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
